import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (token: string, refreshToken: string) => void;
  logout: () => void;
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8889';

  const validateToken = useCallback((token: string): boolean => {
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      // console.log('Token payload:', tokenData);
      const expirationTime = tokenData.exp * 1000;
      // Add a 5-second buffer to prevent edge cases
      return Date.now() < (expirationTime - 5000);
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setAccessToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);
    navigate('/login');
  }, [navigate]);

  const login = useCallback((token: string, newRefreshToken: string) => {
    if (!token || !newRefreshToken) {
      console.error('Invalid login tokens');
      return;
    }

    localStorage.setItem('accessToken', token);
    localStorage.setItem('refreshToken', newRefreshToken);
    setAccessToken(token);
    setRefreshToken(newRefreshToken);
    setIsAuthenticated(true);
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const currentRefreshToken = localStorage.getItem('refreshToken');
    if (!currentRefreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${baseUrl}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: currentRefreshToken,
      });

      if (!response.ok) {
        // Enhanced error logging with status code and response text
        const errorText = await response.text().catch(() => 'No response text');
        console.error(`Token refresh failed with status: ${response.status}, message: ${errorText}`);
        throw new Error(`Token refresh failed: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      
      if (!data.access_token || !data.refresh_token) {
        throw new Error('Invalid token response');
      }

      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('refreshToken', data.refresh_token);
      setIsAuthenticated(true);
      return data.access_token;
    } catch (error) {
      console.error('Error refreshing token:', error);
      logout();
      return null;
    }
  }, [baseUrl, logout]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    const currentAccessToken = localStorage.getItem('accessToken');
    const currentRefreshToken = localStorage.getItem('refreshToken');

    if (!currentAccessToken || !currentRefreshToken) {
      throw new Error('No tokens available');
    }

    try {
      if (!validateToken(currentAccessToken)) {
        const newToken = await refreshAccessToken();
        if (!newToken) {
          throw new Error('Token refresh failed');
        }
        return newToken;
      }

      return currentAccessToken;
    } catch (error) {
      console.error('Error getting access token:', error);
      logout();
      throw error;
    }
  }, [validateToken, refreshAccessToken, logout]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('[AuthContext] Initializing auth...');
      try {
        const storedAccessToken = localStorage.getItem('accessToken');
        const storedRefreshToken = localStorage.getItem('refreshToken');

        console.log('[AuthContext] Tokens found:', { 
          hasAccessToken: !!storedAccessToken, 
          hasRefreshToken: !!storedRefreshToken 
        });

        if (!storedAccessToken || !storedRefreshToken) {
          console.log('[AuthContext] No tokens found, user not authenticated');
          return;
        }

        const isValid = validateToken(storedAccessToken);
        console.log('[AuthContext] Token validation result:', isValid);

        if (isValid) {
          console.log('[AuthContext] Token is valid, setting authenticated state');
          setAccessToken(storedAccessToken);
          setRefreshToken(storedRefreshToken);
          setIsAuthenticated(true);
        } else {
          // Try to refresh the token if it's expired
          console.log('[AuthContext] Token expired, attempting refresh...');
          const newToken = await refreshAccessToken();
          if (newToken) {
            console.log('[AuthContext] Token refreshed successfully');
            setIsAuthenticated(true);
          } else {
            console.log('[AuthContext] Token refresh failed');
          }
        }
      } catch (error) {
        console.error('[AuthContext] Error initializing auth:', error);
        logout();
      } finally {
        console.log('[AuthContext] Initialization complete, isAuthenticated will be:', isAuthenticated);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, [validateToken, refreshAccessToken, logout]);

  // Add an interval to check token expiration
  useEffect(() => {
    let checkTokenInterval: NodeJS.Timeout;

    if (isAuthenticated && accessToken) {
      checkTokenInterval = setInterval(async () => {
        try {
          await getAccessToken();
        } catch (error) {
          console.error('Token validation failed:', error);
          logout();
        }
      }, 30000); // Check every 30 seconds
    }

    return () => {
      if (checkTokenInterval) {
        clearInterval(checkTokenInterval);
      }
    };
  }, [isAuthenticated, accessToken, getAccessToken, logout]);

  if (!isInitialized) {
    return null; // Or a loading spinner
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 